import { useCallback, useEffect, useRef, useState } from "react";
import {
  createPubMission,
  getPubMissionDetail,
  MissionCycle,
  MissionRewardType,
  MissionType,
  PubMissionRewardRequest,
  resetPubMissionProgress,
  SellerPubMissionRequest,
  updatePubMission,
} from "../../../api/pub-mission";

export interface MissionFormReward extends PubMissionRewardRequest {
  key: string;
}

export interface MissionFormState {
  name: string;
  description: string;
  isAlwaysOn: boolean;
  startDate: string;
  endDate: string;
  missionCycle: MissionCycle;
  type: MissionType;
  count: string;
  rewards: MissionFormReward[];
}

const createReward = (
  type: MissionRewardType = MissionRewardType.POINT,
): MissionFormReward => ({
  key: `${Date.now()}-${Math.random()}`,
  type,
  count: type === MissionRewardType.POINT ? 1 : undefined,
  name: type === MissionRewardType.OTHER ? "" : undefined,
});

export const createInitialFormState = (): MissionFormState => ({
  name: "",
  description: "",
  isAlwaysOn: false,
  startDate: "",
  endDate: "",
  missionCycle: MissionCycle.DAILY,
  type: MissionType.ONE_OFF,
  count: "3",
  rewards: [createReward()],
});

const mapDetailToForm = (
  detail: Awaited<ReturnType<typeof getPubMissionDetail>>,
): MissionFormState => ({
  name: detail.name,
  description: detail.description,
  isAlwaysOn: detail.isAlwaysOn,
  startDate: detail.startDate,
  endDate: detail.endDate ?? "",
  missionCycle: detail.missionCycle ?? MissionCycle.DAILY,
  type: detail.type,
  count: detail.count ? String(detail.count) : "3",
  rewards:
    detail.rewards.length > 0
      ? detail.rewards.map((reward, index) => ({
          key: `reward-${index}-${reward.type}-${reward.count ?? reward.name ?? ""}`,
          type: reward.type,
          count: reward.count,
          name: reward.name,
        }))
      : [createReward()],
});

export const useMissionForm = ({
  pubId,
  missionId,
  onSuccess,
  onClose,
}: {
  pubId: number;
  missionId?: number;
  onSuccess: () => Promise<void>;
  onClose: () => void;
}) => {
  const isEdit = Boolean(missionId);
  const [form, setForm] = useState<MissionFormState>(createInitialFormState);
  const [canEdit, setCanEdit] = useState(true);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resetting, setResetting] = useState(false);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const loadDetail = useCallback(async () => {
    if (!missionId || !pubId) {
      setForm(createInitialFormState());
      setCanEdit(true);
      return;
    }

    setLoading(true);
    try {
      const detail = await getPubMissionDetail(missionId, pubId);
      setForm(mapDetailToForm(detail));
      setCanEdit(detail.canEdit);
    } catch (error: any) {
      alert(error?.message || "미션 정보를 불러오지 못했습니다.");
      onCloseRef.current();
    } finally {
      setLoading(false);
    }
  }, [missionId, pubId]);

  useEffect(() => {
    if (!missionId || !pubId) {
      setForm(createInitialFormState());
      setCanEdit(true);
      return;
    }

    let cancelled = false;

    const run = async () => {
      setLoading(true);
      try {
        const detail = await getPubMissionDetail(missionId, pubId);
        if (cancelled) return;
        setForm(mapDetailToForm(detail));
        setCanEdit(detail.canEdit);
      } catch (error: any) {
        if (cancelled) return;
        alert(error?.message || "미션 정보를 불러오지 못했습니다.");
        onCloseRef.current();
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [missionId, pubId]);

  const resetProgress = useCallback(async () => {
    if (!missionId || !pubId) return;

    setResetting(true);
    try {
      await resetPubMissionProgress(missionId, pubId, {
        memo: "미션 진행 상황을 초기화합니다",
      });
      await loadDetail();
      await onSuccess();
      alert("미션 진행 상황이 초기화되었습니다.");
    } catch (error: any) {
      alert(error?.message || "진행 초기화에 실패했습니다.");
      throw error;
    } finally {
      setResetting(false);
    }
  }, [loadDetail, missionId, onSuccess, pubId]);

  const updateField = useCallback(
    <K extends keyof MissionFormState>(key: K, value: MissionFormState[K]) => {
      setForm((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const addReward = useCallback(() => {
    setForm((prev) => {
      const hasPoint = prev.rewards.some(
        (reward) => reward.type === MissionRewardType.POINT,
      );
      return {
        ...prev,
        rewards: [
          ...prev.rewards,
          createReward(
            hasPoint ? MissionRewardType.OTHER : MissionRewardType.POINT,
          ),
        ],
      };
    });
  }, []);

  const removeReward = useCallback((key: string) => {
    setForm((prev) => ({
      ...prev,
      rewards:
        prev.rewards.length <= 1
          ? prev.rewards
          : prev.rewards.filter((reward) => reward.key !== key),
    }));
  }, []);

  const updateReward = useCallback(
    (key: string, patch: Partial<PubMissionRewardRequest>) => {
      setForm((prev) => ({
        ...prev,
        rewards: prev.rewards.map((reward) =>
          reward.key === key ? { ...reward, ...patch } : reward,
        ),
      }));
    },
    [],
  );

  const validate = useCallback((): string | null => {
    if (!form.name.trim()) return "미션명을 입력해주세요.";
    if (!form.description.trim()) return "미션 설명을 입력해주세요.";
    if (!form.startDate) return "시작일을 입력해주세요.";
    if (!form.isAlwaysOn && !form.endDate) return "종료일을 입력해주세요.";
    if (
      form.type === MissionType.CUMULATIVE &&
      (!form.count || Number(form.count) <= 0)
    ) {
      return "목표 횟수를 입력해주세요.";
    }

    const pointRewards = form.rewards.filter(
      (reward) => reward.type === MissionRewardType.POINT,
    );
    if (pointRewards.length > 1) {
      return "매장이용권 보상은 1개만 등록할 수 있습니다.";
    }
    if (
      pointRewards.length === 1 &&
      (!pointRewards[0].count || pointRewards[0].count <= 0)
    ) {
      return "매장이용권 지급 수량을 입력해주세요.";
    }

    for (const reward of form.rewards) {
      if (reward.type === MissionRewardType.OTHER && !reward.name?.trim()) {
        return "기타 보상 이름을 입력해주세요.";
      }
    }

    return null;
  }, [form]);

  const buildRequest = useCallback((): SellerPubMissionRequest => {
    return {
      pubId,
      name: form.name.trim(),
      description: form.description.trim(),
      startDate: form.startDate,
      endDate: form.isAlwaysOn ? undefined : form.endDate,
      isAlwaysOn: form.isAlwaysOn,
      missionCycle: form.isAlwaysOn ? form.missionCycle : undefined,
      type: form.type,
      count:
        form.type === MissionType.CUMULATIVE
          ? Number(form.count)
          : undefined,
      rewards: form.rewards.map(({ type, count, name }) => ({
        type,
        count: type === MissionRewardType.POINT ? Number(count) : undefined,
        name: type === MissionRewardType.OTHER ? name?.trim() : undefined,
      })),
    };
  }, [form, pubId]);

  const submit = useCallback(async () => {
    if (!canEdit) {
      alert("진행 중인 유저가 있어 미션을 수정할 수 없습니다.");
      return;
    }

    const errorMessage = validate();
    if (errorMessage) {
      alert(errorMessage);
      return;
    }

    setSubmitting(true);
    try {
      const request = buildRequest();
      if (isEdit && missionId) {
        await updatePubMission(missionId, request);
      } else {
        await createPubMission(request);
      }
      await onSuccess();
      onClose();
    } catch (error: any) {
      alert(error?.message || "저장에 실패했습니다.");
    } finally {
      setSubmitting(false);
    }
  }, [
    buildRequest,
    canEdit,
    isEdit,
    missionId,
    onClose,
    onSuccess,
    validate,
  ]);

  return {
    form,
    isEdit,
    canEdit,
    loading,
    submitting,
    resetting,
    updateField,
    addReward,
    removeReward,
    updateReward,
    submit,
    resetProgress,
  };
};
